import { roundTo, unique } from '../utilities';
import type { Format } from '../domain/content/entities/valueobjects/Fields';

export const getNumberFormat = (fieldFormat: Format) => {
  let numberFormat = '';

  numberFormat += fieldFormat.number.showSign ? '+' : '-';
  numberFormat += fieldFormat.number.digitGrouping ? ',' : '';
  numberFormat += '.' + fieldFormat.number.decimalPlaces;
  numberFormat += fieldFormat.number.showZero ? '' : '~';
  numberFormat += fieldFormat.number.shorten ? 's' : 'f';

  return numberFormat;
};

export const getOptimalDecimalPlaces = (numbers: number[]) => {
  let prevCount = -1;
  let prevDecimalPlaces = 0;
  let decimalPlaces = 0;

  const baseUniqueness = unique(numbers).length;

  const decimals = numbers.map((number) => countDecimals(number));
  const maxDecimal = Math.max(...decimals);

  if (decimals.every((decimal) => decimal === 0)) return 0;
  if (decimals.every((decimal) => [0, 1].includes(decimal))) return 1;
  if (decimals.every((decimal) => [0, 1, 2].includes(decimal))) return 2;

  for (let idx = 3; idx <= maxDecimal; idx++) {
    const uniques = unique(numbers.map((num) => roundTo(num, idx)));
    const maxUniqueDecimal = Math.max(...uniques.map((number) => countDecimals(number)));

    // we do not improve anymore
    if (uniques.length === prevCount && maxUniqueDecimal === prevDecimalPlaces) {
      decimalPlaces = maxUniqueDecimal;
      break;
    }

    // we found the optimal decimal places
    if (uniques.length === baseUniqueness) {
      decimalPlaces = idx;
      break;
    }

    prevCount = uniques.length;
    prevDecimalPlaces = maxUniqueDecimal;
  }

  return decimalPlaces;
};

/**
 * gives the integer value of decimal places
 * @param  {number} value  number to test
 * @return {number}        decimal places
 */
export const countDecimals = (value: number) => {
  if (Math.floor(value) === value) return 0;
  const numSplit = String(value).split('.');
  if (numSplit.length > 1) {
    const decimals = numSplit[1].length ?? 0;
    return decimals;
  } else {
    return 0;
  }
};

/**
 * converts a string value into a float value
 * if it is possible
 * @return a float value or the string value if no conversion was possible
 */
export const convertStringToValueIfPossible = (stringValue, preferedLocale = 'de-DE') => {
  let retObj = {
    value: undefined,
    prefix: undefined,
    suffix: undefined,
  };

  let val = stringValue ? stringValue.toString().trim() : null;
  //console.log(val,preferedLocale);
  if (val) {
    // Test first for prefered Locale
    if (preferedLocale === 'de-DE') {
      retObj = findGermanNumber(val);
      //console.log("[Testing DE VAL FIRST]",retObj)
      if (retObj.value !== undefined) return retObj;
      retObj = findEnglishNumber(val);
      if (retObj.value !== undefined) return retObj;
    } else {
      // console.log("[Testing ENGLISCH VAL FIRST]",retObj)
      retObj = findEnglishNumber(val);
      if (retObj.value !== undefined) return retObj;
      retObj = findGermanNumber(val);
      if (retObj.value !== undefined) return retObj;
    }
  }
  retObj.value = val;
  return retObj;
};

/**
 * given a list of decimal places returns atm
 * average decimal part - this is a function so we can change the algorithm
 * to for example max and reuse it on separate occasions
 * @param  decimalPlacesList - list of decimal places (array of integers)
 * @return Integer
 */
export const getDecimalPlaces = (decimalPlacesList) => {
  return Math.round(
    decimalPlacesList.reduce((accumulator, obj) => {
      return accumulator + obj;
    }, 0) / decimalPlacesList.length,
  );
};

// warning do not use global otherwise this breaks /g -> hidden state amazingness!
const numberRegexDE = /^([€$]?\s?)[+]?([-]?([0-9]{1,3}(\.[0-9]{3})*(,[0-9]+)?|\d*\,\d+|\d+))(\s?[%]?)$/;
const numberRegexEN = /^([€$]?\s?)[+]?([-]?([0-9]{1,3}(,[0-9]{3})*(\.[0-9]+)?|\d*\.\d+|\d+))(\s?[%]?)$/;

const findEnglishNumber = (numStr) => {
  const retObj = {
    value: undefined,
    prefix: undefined,
    suffix: undefined,
  };

  const match = numberRegexEN.exec(numStr);
  if (match) {
    let num = match[2];
    num = num.replace(/[,\+]/g, ''); // replace + and , (thousandSeparator) with nothing

    retObj.value = parseFloat(num);
    retObj.prefix = match[1];
    retObj.suffix = match[5];
  }

  return retObj;
};

const findGermanNumber = (numStr) => {
  const retObj = {
    value: undefined,
    prefix: undefined,
    suffix: undefined,
  };

  const match = numberRegexDE.exec(numStr);
  if (match) {
    let num = match[2];
    num = num.replace(/[\.\+]/g, ''); // replace , (comma separator) with . so it can be parsed
    num = num.replace(/,/g, '.');

    retObj.value = parseFloat(num);
    retObj.prefix = match[1];
    retObj.suffix = match[5];
  }

  return retObj;
};

interface NumberDetection {
  value: number;
  prefix: string;
  suffix: string;
  locale: 'de-DE' | 'en-US';
}

/**
 * locale muss gut sien wenn nicht dann kann sein
 * 3,000 -> entscheidet welche locale
 * de-DE -> 3.000
 * en-US -> 3000
 *
 * @param {*} numStr
 * @param {*} locale
 */
export const stringToNumber = (numStr: string | number, locale: string = 'de-DE'): NumberDetection => {
  const detection: NumberDetection = {
    value: undefined,
    prefix: undefined,
    suffix: undefined,
    locale: undefined,
  };

  if (numStr === undefined || numStr === null) return detection;
  // do not convert to string if we are a valid number already
  if (typeof numStr === 'number') {
    detection.value = numStr;
    return detection;
  }

  numStr = String(numStr).trim();

  if (locale === 'de-DE') {
    if (processNumberDE(numStr, detection)) return detection;
    if (processNumberEN(numStr, detection)) return detection;
  } else {
    if (processNumberEN(numStr, detection)) return detection;
    if (processNumberDE(numStr, detection)) return detection;
  }

  return detection;
};

const processNumberDE = (numStr: string, detection: NumberDetection) => {
  // strip all whitespaces
  numStr = numStr.replace(/\s/, '');

  const match = numberRegexDE.exec(numStr);
  if (match) {
    let num = match[2];
    num = num.replace(/[\.\+]/g, ''); // replace , (comma separator) with . so it can be parsed
    num = num.replace(/,/g, '.');

    detection.value = parseFloat(num);
    detection.prefix = match[1];
    detection.suffix = match[5];
    detection.locale = 'de-DE';

    return true;
  }

  return false;
};

const processNumberEN = (numStr: string, detection: NumberDetection) => {
  // strip all whitespaces
  numStr = numStr.replace(/\s/, '');

  const match = numberRegexEN.exec(numStr);
  if (match) {
    let num = match[2];
    num = num.replace(/[,\+]/g, ''); // replace + and , (thousandSeparator) with nothing

    detection.value = parseFloat(num);
    detection.prefix = match[1];
    detection.suffix = match[5];
    detection.locale = 'en-US';

    return true;
  }

  return false;
};

/**
 * Split up string by a given delimiter character and remove all whitespaces at start and end
 * 
 * @param fieldValue String value create multiple values from
 * @param delimiter Character to split up the item value to get multiple values
 * @returns 
 */
export const parseMultiFilterValue = (fieldValue: string, delimiter: string): string[] => {
  return fieldValue.split(delimiter).filter(Boolean).map((value) => value.trim());
};

/**
 * Get the values of a specific fieldname from the dataset
 * hand it over to splitter function and flat out the array of strings
 * to have a unique value list by a given delimiter
 * 
 * @param data Dataset 
 * @param fieldName Property name of the value within the data item
 * @param delimiter Character to split up the item value to get multiple values
 * @returns 
 */
export const parseMultiFilterData = (data: any[], fieldName: string, delimiter: string): string[] => {
  return data.filter((item) => !!item[fieldName]).flatMap((item) => parseMultiFilterValue(item[fieldName], delimiter));
};
